import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle, ZoomIn, ZoomOut, FileText, Download } from "lucide-react";
import DOMPurify from "dompurify";
import { carica, inCache } from "../attachCache";
import PdfPreview from "./PdfPreview";
import Zoomabile from "./Zoomabile";

const IMG = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif", "heic", "heif", "ico"];
const TXT = ["txt", "csv", "log", "json", "xml", "md", "ini", "cfg", "conf", "yml", "yaml", "js", "py", "css", "sql", "eml", "ics", "vcf", "rtf"];
const AUDIO = ["mp3", "wav", "ogg", "m4a", "aac", "flac", "opus", "weba"];
const VIDEO = ["mp4", "webm", "mov", "m4v", "ogv", "3gp"];
const SHEET = ["xlsx", "xls", "xlsm", "ods", "tsv"];
const DOCX = ["docx", "docm", "dotx"];
const HTML = ["html", "htm", "mhtml"];

export function tipoAnteprima(tipo) {
  const t = (tipo || "").toLowerCase();
  if (IMG.includes(t)) return "img";
  if (t === "pdf") return "pdf";
  if (TXT.includes(t)) return "text";
  if (AUDIO.includes(t)) return "audio";
  if (VIDEO.includes(t)) return "video";
  if (SHEET.includes(t)) return "sheet";
  if (DOCX.includes(t)) return "docx";
  if (HTML.includes(t)) return "html";
  return "altro";
}

function Stato({ icon: Icon, testo, spin }) {
  return (
    <div className="preview-fs-state" data-testid="preview-state">
      <Icon size={26} strokeWidth={2.2} className={spin ? "spin" : ""} /> {testo}
    </div>
  );
}

// Immagine: subito la miniatura leggera (dalla cache se già scaricata), originale solo quando si ingrandisce
// (tasto lente o due dita: Zoomabile, 1x–5x)
function ImgPreview({ id, part, nome }) {
  const [thumb, setThumb] = useState(() => inCache(id, part, "thumb")?.url || null);
  const [full, setFull] = useState(() => inCache(id, part, "full")?.url || null);
  const [zoom, setZoom] = useState(false);
  const [origine, setOrigine] = useState(false);
  const [caricoFull, setCaricoFull] = useState(false);
  const [errore, setErrore] = useState("");
  useEffect(() => {
    let vivo = true;
    carica(id, part, "thumb").then((e) => vivo && setThumb(e.url)).catch((e) => vivo && setErrore(e.message));
    return () => { vivo = false; };
  }, [id, part]);
  useEffect(() => {
    if ((!zoom && !origine) || full) return;
    let vivo = true;
    setCaricoFull(true);
    carica(id, part, "full").then((e) => vivo && setFull(e.url)).catch((e) => vivo && setErrore(e.message)).finally(() => vivo && setCaricoFull(false));
    return () => { vivo = false; };
  }, [zoom, origine, full, id, part]);
  if (errore) return <Stato icon={AlertTriangle} testo={`Anteprima non riuscita: ${errore}`} />;
  const src = (zoom || origine) && full ? full : thumb;
  return (
    <>
      <Zoomabile max={5} onScala={(s) => s > 1 && setOrigine(true)} testid="preview-zoom-wrap">
        <div className={`preview-fs-imgwrap ${zoom ? "zoom" : ""}`} data-testid="preview-imgwrap">
          {!src && <Stato icon={Loader2} testo={`Apro ${nome}...`} spin />}
          {src && (
            <img
              className={`preview-fs-img ${zoom ? "full" : ""}`}
              src={src}
              alt={nome}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onClick={() => setZoom((z) => !z)}
              title={zoom ? "Riduci" : "Ingrandisci (carica l'originale)"}
              data-testid="preview-image"
            />
          )}
        </div>
      </Zoomabile>
      {src && (
        <button className="icon-btn preview-fs-zoom" onClick={() => setZoom((z) => !z)} aria-label={zoom ? "Riduci" : "Ingrandisci"} data-testid="preview-zoom">
          <IconaZoom caricoFull={caricoFull} zoom={zoom} />
        </button>
      )}
    </>
  );
}

// Media con indicatore di caricamento immediato; usa il blob in cache appena disponibile
function Frame({ id, part, nome, kind, url }) {
  const [src, setSrc] = useState(() => inCache(id, part)?.url || null);
  const [pronto, setPronto] = useState(false);
  useEffect(() => {
    let vivo = true;
    carica(id, part).then((e) => vivo && setSrc(e.url)).catch(() => vivo && setSrc(url)); // in caso di errore: URL diretto
    return () => { vivo = false; };
  }, [id, part, url]);
  return (
    <div className="preview-fs-framewrap">
      {(!src || !pronto) && <Stato icon={Loader2} testo={`Apro ${nome}...`} spin />}
      {src && kind === "audio" && <audio className="preview-fs-audio" src={src} controls autoPlay onLoadedMetadata={() => setPronto(true)} onCanPlay={() => setPronto(true)} data-testid="preview-audio" />}
      {src && kind === "video" && <video className={`preview-fs-video ${pronto ? "" : "nascosto"}`} src={src} controls autoPlay playsInline onLoadedData={() => setPronto(true)} data-testid="preview-video" />}
    </div>
  );
}

// PDF: dal blob in cache, prima pagina subito e le altre scorrendo
function Pdf({ id, part, nome, onStampa, onScarica }) {
  const [blob, setBlob] = useState(() => inCache(id, part)?.blob || null);
  const [errore, setErrore] = useState("");
  useEffect(() => {
    let vivo = true;
    carica(id, part).then((e) => vivo && setBlob(e.blob)).catch((e) => vivo && setErrore(e.message));
    return () => { vivo = false; };
  }, [id, part]);
  if (errore) return <Stato icon={AlertTriangle} testo={`Anteprima non riuscita: ${errore}`} />;
  if (!blob) return <Stato icon={Loader2} testo={`Apro ${nome}...`} spin />;
  return (
    <Zoomabile max={4} testid="preview-zoom-wrap">
      <PdfPreview blob={blob} nome={nome} onStampa={onStampa} onScarica={onScarica} />
    </Zoomabile>
  );
}

// Formato senza visualizzatore: scheda informativa, il download parte SOLO se lo chiedi
function Altro({ nome, tipo, dimensione, onScarica }) {
  return (
    <div className="preview-fs-card" data-testid="preview-generic">
      <div className="preview-fs-icon">
        <FileText size={56} strokeWidth={1.8} />
        <span className="pdf-badge">{(tipo || "FILE").toUpperCase().slice(0, 4)}</span>
      </div>
      <div className="preview-fs-card-name">{nome}</div>
      <div className="preview-fs-card-meta">{dimensione}</div>
      <p className="preview-fs-card-text">Questo formato non ha un'anteprima in pagina. Puoi scaricarlo e aprirlo con l'app dedicata.</p>
      <button className="wf-btn primary preview-fs-download" onClick={onScarica} data-testid="preview-download-btn">
        <Download size={18} strokeWidth={2.6} /> Scarica
      </button>
    </div>
  );
}

// Anteprima "in pagina" di qualsiasi formato: immagini, PDF, testo/CSV/EML, audio, video,
// fogli Excel/ODS (SheetJS) e Word .docx (mammoth) convertiti in HTML nel browser, HTML in iframe isolato.
// Icona del tasto zoom: caricamento, riduci o ingrandisci
function IconaZoom({ caricoFull, zoom }) {
  if (caricoFull) return <Loader2 size={20} className="spin" />;
  return zoom ? <ZoomOut size={20} strokeWidth={2.4} /> : <ZoomIn size={20} strokeWidth={2.4} />;
}

export default function FilePreview({ id, part, url, tipo, nome, dimensione, onScarica, onStampa }) {
  const kind = tipoAnteprima(tipo);
  const [stato, setStato] = useState("carico");
  const [contenuto, setContenuto] = useState(null);
  const [errore, setErrore] = useState("");
  const [frameLoad, setFrameLoad] = useState(0);

  useEffect(() => {
    if (!["text", "sheet", "docx", "html"].includes(kind)) return;
    let vivo = true;
    setStato("carico");
    setContenuto(null);
    (async () => {
      try {
        const { blob } = await carica(id, part);
        if (kind === "text" || kind === "html") {
          const testo = await blob.text();
          if (vivo) setContenuto(kind === "html" ? testo : testo.slice(0, 2_000_000));
        } else if (kind === "sheet") {
          const XLSX = await import("xlsx");
          const wb = XLSX.read(await blob.arrayBuffer(), { type: "array" });
          const fogli = wb.SheetNames.map((n) => ({ nome: n, html: XLSX.utils.sheet_to_html(wb.Sheets[n], { editable: false }) }));
          if (vivo) setContenuto(fogli);
        } else if (kind === "docx") {
          const mammoth = await import("mammoth/mammoth.browser");
          const res = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() });
          if (vivo) setContenuto(res.value);
        }
        if (vivo) setStato("ok");
      } catch (e) {
        if (vivo) {
          setErrore(e?.message || "Errore di lettura");
          setStato("errore");
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [id, part, kind]);

  if (kind === "img") return <ImgPreview id={id} part={part} nome={nome} />;
  if (kind === "pdf") return <Pdf id={id} part={part} nome={nome} onStampa={onStampa} onScarica={onScarica} />;
  if (kind === "altro") return <Altro nome={nome} tipo={tipo} dimensione={dimensione} onScarica={onScarica} />;
  if (["audio", "video"].includes(kind)) return <Frame id={id} part={part} nome={nome} kind={kind} url={url} />;
  if (stato === "carico") return <Stato icon={Loader2} testo={`Apro ${nome}...`} spin />;
  if (stato === "errore") return <Stato icon={AlertTriangle} testo={`Anteprima non riuscita: ${errore}`} />;
  // iframe senza script (sandbox senza allow-scripts) ma same-origin, così il gesto a due dita arriva anche da dentro
  const frame = (srcDoc, testid) => <iframe className="preview-fs-frame preview-fs-html" sandbox="allow-same-origin" srcDoc={srcDoc} title={nome} onLoad={() => setFrameLoad((n) => n + 1)} data-testid={testid} />;
  const zoomabile = (nodo) => <Zoomabile max={4} frameLoad={frameLoad} testid="preview-zoom-wrap">{nodo}</Zoomabile>;
  if (kind === "text") return zoomabile(<pre className="preview-fs-text" data-testid="preview-text">{contenuto}</pre>);
  if (kind === "html") return zoomabile(frame(contenuto, "preview-html"));
  if (kind === "docx") return zoomabile(frame(`<!doctype html><meta charset="utf-8"><style>body{font-family:Arial,sans-serif;padding:24px 32px;max-width:900px;margin:0 auto;line-height:1.5;color:#111}img{max-width:100%}table{border-collapse:collapse}td,th{border:1px solid #ccc;padding:4px 8px}</style>${contenuto}`, "preview-docx"));
  if (kind === "sheet") {
    return zoomabile(
      <div className="preview-fs-sheets" data-testid="preview-sheet">
        {contenuto.map((f, i) => (
          <details key={f.nome} open={i === 0}>
            <summary>{f.nome}</summary>
            <div className="preview-fs-sheet" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(f.html) }} />
          </details>
        ))}
      </div>
    );
  }
  return null;
}
